import { DestroyRef, Injectable } from '@angular/core';
import { Observable, defer, finalize } from 'rxjs';
import {
  ActiveLoaderRecord,
  ActiveLoaderStatus,
  LoaderScope,
  LoaderStartConfig,
  LoaderToken,
} from '../models/loading.models';
import { resolveLoaderStartConfig } from '../utils/loading-defaults.util';
import { LoaderRegistryService } from './loader-registry.service';

type LoaderFinalStatus = Extract<
  ActiveLoaderStatus,
  'completed' | 'cancelled' | 'timed-out'
>;

@Injectable({
  providedIn: 'root',
})
export class LoaderOrchestratorService {
  // Timer handles stay outside the registry so store state remains serializable.
  private readonly delayTimersByTokenId = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly hideTimersByTokenId = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly timeoutTimersByTokenId = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private tokenSequence = 0;

  constructor(private readonly loaderRegistryService: LoaderRegistryService) {}

  start(loaderStartConfig: LoaderStartConfig): LoaderToken {
    const resolvedLoaderStartConfig = resolveLoaderStartConfig(loaderStartConfig);
    const startedAtEpochMs = this.getNowEpochMs();
    const loaderToken = this.createLoaderToken(resolvedLoaderStartConfig);
    const delayMs = resolvedLoaderStartConfig.delayMs ?? 0;

    const activeLoaderRecord: ActiveLoaderRecord = {
      token: loaderToken,
      config: resolvedLoaderStartConfig,
      status: delayMs > 0 ? 'pending-delay' : 'visible',
      startedAtEpochMs,
      visibleAtEpochMs: delayMs > 0 ? undefined : startedAtEpochMs,
      refCount: 1,
      visibleMode: resolvedLoaderStartConfig.mode,
      resolvedPriority: resolvedLoaderStartConfig.priority ?? 0,
    };

    this.loaderRegistryService.addRecord(activeLoaderRecord);
    this.scheduleVisibility(activeLoaderRecord);
    this.scheduleTimeout(activeLoaderRecord);

    return loaderToken;
  }

  stop(loaderToken: LoaderToken): void {
    const activeLoaderRecord = this.loaderRegistryService.getRecordSnapshot(
      loaderToken.id
    );

    if (!activeLoaderRecord) {
      return;
    }

    if (activeLoaderRecord.refCount > 1) {
      this.loaderRegistryService.updateRecordRefCount(
        loaderToken.id,
        activeLoaderRecord.refCount - 1
      );
      return;
    }

    this.finalizeLoader(loaderToken.id, 'completed');
  }

  withLoader<T>(
    source$: Observable<T>,
    loaderStartConfig: LoaderStartConfig
  ): Observable<T> {
    return defer(() => {
      const loaderToken = this.start(loaderStartConfig);

      return source$.pipe(finalize(() => this.stop(loaderToken)));
    });
  }

  incrementReference(
    referenceKey: string,
    loaderStartConfig: LoaderStartConfig
  ): LoaderToken {
    const primaryReferenceTokenId = this.getPrimaryReferenceTokenId(referenceKey);

    if (!primaryReferenceTokenId) {
      return this.start({
        ...loaderStartConfig,
        referenceKey,
      });
    }

    const activeLoaderRecord = this.loaderRegistryService.getRecordSnapshot(
      primaryReferenceTokenId
    );

    if (!activeLoaderRecord) {
      return this.start({
        ...loaderStartConfig,
        referenceKey,
      });
    }

    this.loaderRegistryService.updateRecordRefCount(
      primaryReferenceTokenId,
      activeLoaderRecord.refCount + 1
    );
    this.clearHideTimer(primaryReferenceTokenId);

    return activeLoaderRecord.token;
  }

  decrementReference(referenceKey: string): void {
    const primaryReferenceTokenId = this.getPrimaryReferenceTokenId(referenceKey);

    if (!primaryReferenceTokenId) {
      return;
    }

    this.stop({ id: primaryReferenceTokenId });
  }

  startUntilDestroyed(
    destroyRef: DestroyRef,
    loaderStartConfig: LoaderStartConfig
  ): LoaderToken {
    const loaderToken = this.start(loaderStartConfig);
    this.bindTokenToDestroyRef(destroyRef, loaderToken);

    return loaderToken;
  }

  incrementReferenceUntilDestroyed(
    destroyRef: DestroyRef,
    referenceKey: string,
    loaderStartConfig: LoaderStartConfig
  ): LoaderToken {
    const loaderToken = this.incrementReference(referenceKey, loaderStartConfig);

    destroyRef.onDestroy(() => {
      this.decrementReference(referenceKey);
    });

    return loaderToken;
  }

  cancel(loaderToken: LoaderToken): void {
    this.cancelByTokenId(loaderToken.id);
  }

  clearScope(loaderScope: LoaderScope): void {
    this.getActiveLoaderRecords().forEach((activeLoaderRecord) => {
      if (
        activeLoaderRecord.config.scope.type === loaderScope.type &&
        activeLoaderRecord.config.scope.key === loaderScope.key
      ) {
        this.cancelByTokenId(activeLoaderRecord.token.id);
      }
    });
  }

  clearGroup(groupId: string): void {
    this.getActiveLoaderRecords().forEach((activeLoaderRecord) => {
      if (activeLoaderRecord.config.groupId === groupId) {
        this.cancelByTokenId(activeLoaderRecord.token.id);
      }
    });
  }

  clearAll(): void {
    this.getActiveLoaderRecords().forEach((activeLoaderRecord) => {
      this.cancelByTokenId(activeLoaderRecord.token.id);
    });
  }

  private scheduleVisibility(activeLoaderRecord: ActiveLoaderRecord): void {
    const delayMs = activeLoaderRecord.config.delayMs ?? 0;

    if (delayMs <= 0) {
      return;
    }

    const delayTimerHandle = setTimeout(() => {
      const currentLoaderRecord = this.loaderRegistryService.getRecordSnapshot(
        activeLoaderRecord.token.id
      );

      if (!currentLoaderRecord || currentLoaderRecord.status !== 'pending-delay') {
        return;
      }

      this.loaderRegistryService.markRecordVisible(
        activeLoaderRecord.token.id,
        this.getNowEpochMs()
      );
      this.delayTimersByTokenId.delete(activeLoaderRecord.token.id);
    }, delayMs);

    this.delayTimersByTokenId.set(activeLoaderRecord.token.id, delayTimerHandle);
  }

  private scheduleTimeout(activeLoaderRecord: ActiveLoaderRecord): void {
    const timeoutMs = activeLoaderRecord.config.timeoutMs;

    if (!timeoutMs || timeoutMs <= 0) {
      return;
    }

    const timeoutTimerHandle = setTimeout(() => {
      const currentLoaderRecord = this.loaderRegistryService.getRecordSnapshot(
        activeLoaderRecord.token.id
      );

      if (!currentLoaderRecord) {
        return;
      }

      this.finalizeLoader(activeLoaderRecord.token.id, 'timed-out');
    }, timeoutMs);

    this.timeoutTimersByTokenId.set(
      activeLoaderRecord.token.id,
      timeoutTimerHandle
    );
  }

  private finalizeLoader(tokenId: string, finalStatus: LoaderFinalStatus): void {
    const activeLoaderRecord = this.loaderRegistryService.getRecordSnapshot(tokenId);

    if (!activeLoaderRecord) {
      return;
    }

    this.clearDelayTimer(tokenId);
    this.clearTimeoutTimer(tokenId);

    if (activeLoaderRecord.status === 'pending-delay') {
      this.completeAndRemoveRecord(tokenId, finalStatus);
      return;
    }

    const visibleAtEpochMs =
      activeLoaderRecord.visibleAtEpochMs ?? activeLoaderRecord.startedAtEpochMs;
    const minVisibleMs = activeLoaderRecord.config.minVisibleMs ?? 0;
    const remainingVisibleMs = Math.max(
      visibleAtEpochMs + minVisibleMs - this.getNowEpochMs(),
      0
    );

    if (remainingVisibleMs === 0) {
      this.completeAndRemoveRecord(tokenId, finalStatus);
      return;
    }

    this.clearHideTimer(tokenId);

    const hideTimerHandle = setTimeout(() => {
      this.completeAndRemoveRecord(tokenId, finalStatus);
    }, remainingVisibleMs);

    this.hideTimersByTokenId.set(tokenId, hideTimerHandle);
  }

  private completeAndRemoveRecord(
    tokenId: string,
    finalStatus: LoaderFinalStatus
  ): void {
    if (!this.loaderRegistryService.getRecordSnapshot(tokenId)) {
      this.clearAllTimers(tokenId);
      return;
    }

    this.loaderRegistryService.finalizeRecord(
      tokenId,
      finalStatus,
      this.getNowEpochMs()
    );
    this.loaderRegistryService.removeRecord(tokenId);
    this.clearAllTimers(tokenId);
  }

  private getPrimaryReferenceTokenId(referenceKey: string): string | undefined {
    return this.loaderRegistryService.getReferenceTokenIds(referenceKey)[0];
  }

  private cancelByTokenId(tokenId: string): void {
    const activeLoaderRecord = this.loaderRegistryService.getRecordSnapshot(tokenId);

    if (!activeLoaderRecord) {
      return;
    }

    this.loaderRegistryService.finalizeRecord(
      tokenId,
      'cancelled',
      this.getNowEpochMs()
    );
    this.loaderRegistryService.removeRecord(tokenId);
    this.clearAllTimers(tokenId);
  }

  private bindTokenToDestroyRef(
    destroyRef: DestroyRef,
    loaderToken: LoaderToken
  ): void {
    destroyRef.onDestroy(() => {
      this.stop(loaderToken);
    });
  }

  private getActiveLoaderRecords(): ActiveLoaderRecord[] {
    return Object.values(this.loaderRegistryService.getSnapshot().activeByTokenId);
  }

  private createLoaderToken(loaderStartConfig: LoaderStartConfig): LoaderToken {
    this.tokenSequence += 1;

    const correlationId = loaderStartConfig.metadata?.['correlationId'];

    return {
      id: `loader-${Date.now()}-${this.tokenSequence}`,
      groupId: loaderStartConfig.groupId,
      correlationId:
        typeof correlationId === 'string' ? correlationId : undefined,
    };
  }

  private clearAllTimers(tokenId: string): void {
    this.clearDelayTimer(tokenId);
    this.clearHideTimer(tokenId);
    this.clearTimeoutTimer(tokenId);
  }

  private clearDelayTimer(tokenId: string): void {
    const delayTimerHandle = this.delayTimersByTokenId.get(tokenId);

    if (!delayTimerHandle) {
      return;
    }

    clearTimeout(delayTimerHandle);
    this.delayTimersByTokenId.delete(tokenId);
  }

  private clearHideTimer(tokenId: string): void {
    const hideTimerHandle = this.hideTimersByTokenId.get(tokenId);

    if (!hideTimerHandle) {
      return;
    }

    clearTimeout(hideTimerHandle);
    this.hideTimersByTokenId.delete(tokenId);
  }

  private clearTimeoutTimer(tokenId: string): void {
    const timeoutTimerHandle = this.timeoutTimersByTokenId.get(tokenId);

    if (!timeoutTimerHandle) {
      return;
    }

    clearTimeout(timeoutTimerHandle);
    this.timeoutTimersByTokenId.delete(tokenId);
  }

  private getNowEpochMs(): number {
    return Date.now();
  }
}
